import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, Pencil, Phone, PawPrint, Trash2, UserRound, UserRoundCheck, MapPin, MessageCircle, MessageCircleOff } from "lucide-react";
import { CrudPage } from "../components/crud/CrudPage";
import { DomicilioCampos } from "../components/ui/DomicilioCampos";
import { ModalBajaCliente } from "../components/clientes/ModalBajaCliente";
import { api } from "../api/client";

interface Cliente {
  id: string;
  nombre: string;
  telefono?: string;
  email?: string;
  ciudad?: string;
  estado?: string;
  notificacionesWhatsapp?: boolean;
  mascotas?: { id: string }[];
}

// Iniciales del nombre del cliente para el avatar de la tarjeta (ej. "Juan
// Pérez" -> "JP"), igual que se ve en el resto de apps que usamos a diario.
function iniciales(nombre: string): string {
  const partes = nombre.trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return "?";
  if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();
  return (partes[0][0] + partes[1][0]).toUpperCase();
}

export default function Clientes() {
  const navigate = useNavigate();
  // Catálogo de regímenes fiscales del SAT, para poder facturarle (CFDI 4.0)
  // a este cliente más adelante desde Facturación. Si no carga (por ejemplo
  // sin conexión), el campo simplemente se queda sin opciones y no bloquea
  // nada del resto del formulario.
  const [regimenesFiscales, setRegimenesFiscales] = useState<{ value: string; label: string }[]>([]);
  const recargarRef = useRef<(() => void) | null>(null);
  const [clienteABajar, setClienteABajar] = useState<Cliente | null>(null);

  useEffect(() => {
    api
      .get("/facturacion/catalogos")
      .then((res) => setRegimenesFiscales(res.data?.regimenesFiscales ?? []))
      .catch(() => {});
  }, []);

  return (
    <>
      <div className="mb-4 flex items-start gap-3 rounded-2xl border border-brand-100 bg-brand-50/60 p-4">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white text-brand-600 shadow-sm">
          <UserRoundCheck size={18} />
        </div>
        <p className="text-sm text-slate-600">
          <span className="font-semibold text-slate-700">Aquí administras a los tutores de tus pacientes.</span> Usa{" "}
          <Pencil size={12} className="inline -mt-0.5" /> para editar sus datos de contacto, toca el teléfono o correo
          para contactarlos directo, y da clic en <span className="font-semibold text-slate-700">"Ver mascotas"</span>{" "}
          de cada tarjeta para consultar el expediente de sus mascotas.
        </p>
      </div>

      <CrudPage<Cliente>
        resource="/clientes"
        seccion="clientes"
        titulo="Clientes"
        icono={<UserRound size={20} />}
        descripcion="Tutores de mascotas registrados en tu negocio."
        nombreSingular="cliente"
        onEliminarPersonalizado={(row) => setClienteABajar(row)}
        recargarRef={recargarRef}
        vista="tarjetas"
        renderTarjeta={(r, { puedeEditar, puedeEliminar, onEditar, onEliminar }) => {
          const totalMascotas = r.mascotas?.length ?? 0;
          const ubicacion = [r.ciudad, r.estado].filter(Boolean).join(", ");
          return (
            <div className="card flex h-full flex-col overflow-hidden !p-0 transition-transform duration-200 hover:-translate-y-0.5 hover:border-brand-200">
              <div className="relative flex h-24 items-center justify-center bg-brand-50">
                <div className="absolute inset-x-0 top-0 h-1.5 bg-brand-400" />
                <div className="flex h-16 w-16 items-center justify-center rounded-full border-4 border-white bg-brand-500 text-lg font-semibold text-white shadow-sm">
                  {iniciales(r.nombre)}
                </div>
                {(puedeEditar || puedeEliminar) && (
                  <div className="absolute right-2 top-3 flex gap-1">
                    {puedeEditar && (
                      <button
                        className="flex h-7 w-7 items-center justify-center rounded-full bg-white/90 text-slate-500 shadow-sm hover:bg-white hover:text-slate-700"
                        title="Editar"
                        onClick={onEditar}
                      >
                        <Pencil size={13} />
                      </button>
                    )}
                    {puedeEliminar && (
                      <button
                        className="flex h-7 w-7 items-center justify-center rounded-full bg-white/90 text-red-400 shadow-sm hover:bg-white hover:text-red-600"
                        title="Dar de baja"
                        onClick={onEliminar}
                      >
                        <Trash2 size={13} />
                      </button>
                    )}
                  </div>
                )}
              </div>

              <div className="flex flex-1 flex-col gap-2 p-4">
                <p className="text-center font-semibold text-slate-800">{r.nombre}</p>

                <div className="space-y-1.5">
                  {r.telefono ? (
                    <a
                      href={`tel:${r.telefono}`}
                      className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-brand-600 hover:underline"
                      title="Llamar"
                    >
                      <Phone size={13} className="shrink-0 text-slate-300" />
                      <span className="truncate">{r.telefono}</span>
                    </a>
                  ) : (
                    <p className="flex items-center gap-1.5 text-xs text-slate-400">
                      <Phone size={13} className="shrink-0 text-slate-300" /> Sin teléfono
                    </p>
                  )}
                  {r.email ? (
                    <a
                      href={`mailto:${r.email}`}
                      className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-brand-600 hover:underline"
                      title="Enviar correo"
                    >
                      <Mail size={13} className="shrink-0 text-slate-300" />
                      <span className="truncate">{r.email}</span>
                    </a>
                  ) : (
                    <p className="flex items-center gap-1.5 text-xs text-slate-400">
                      <Mail size={13} className="shrink-0 text-slate-300" /> Sin correo
                    </p>
                  )}
                  {ubicacion && (
                    <p className="flex items-center gap-1.5 text-xs text-slate-500">
                      <MapPin size={13} className="shrink-0 text-slate-300" />
                      <span className="truncate">{ubicacion}</span>
                    </p>
                  )}
                </div>

                <span
                  className={`badge w-fit ${r.notificacionesWhatsapp === false ? "bg-slate-100 text-slate-400" : "bg-emerald-50 text-emerald-600"}`}
                  title={r.notificacionesWhatsapp === false ? "No recibe avisos por WhatsApp" : "Recibe avisos por WhatsApp"}
                >
                  {r.notificacionesWhatsapp === false ? <MessageCircleOff size={12} /> : <MessageCircle size={12} />}
                  {r.notificacionesWhatsapp === false ? "Sin avisos WhatsApp" : "Avisos WhatsApp activos"}
                </span>

                <button
                  className="mt-auto flex items-center justify-center gap-1.5 rounded-xl border border-slate-100 bg-slate-50 py-2 text-xs font-medium text-brand-600 hover:bg-brand-50"
                  onClick={() => navigate(`/mascotas?clienteId=${r.id}`)}
                >
                  <PawPrint size={13} />
                  Ver mascotas {totalMascotas > 0 ? `(${totalMascotas})` : ""}
                </button>
              </div>
            </div>
          );
        }}
        campos={[
          { name: "nombre", label: "Nombre completo", required: true },
          { name: "telefono", label: "Teléfono", type: "tel" },
          { name: "email", label: "Correo", type: "email" },
          {
            name: "calle",
            label: "Domicilio",
            type: "custom",
            colSpan2: true,
            render: (valores, onChange) => (
              <DomicilioCampos
                valores={{
                  calle: valores.calle ?? "",
                  numero: valores.numero ?? "",
                  codigoPostal: valores.codigoPostal ?? "",
                  colonia: valores.colonia ?? "",
                  ciudad: valores.ciudad ?? "",
                  estado: valores.estado ?? "",
                  pais: valores.pais ?? "México",
                }}
                onChange={(campo, valor) => onChange(campo, valor)}
              />
            ),
          },
          { name: "numero", label: "Número", ocultarEnFormulario: true },
          { name: "codigoPostal", label: "Código postal", ocultarEnFormulario: true },
          { name: "colonia", label: "Colonia", ocultarEnFormulario: true },
          { name: "ciudad", label: "Ciudad", ocultarEnFormulario: true },
          { name: "estado", label: "Estado", ocultarEnFormulario: true },
          { name: "pais", label: "País", default: "México", ocultarEnFormulario: true },
          {
            name: "notificacionesWhatsapp",
            label: "¿Desea recibir notificaciones por WhatsApp de citas y servicios?",
            type: "checkbox",
            default: true,
            colSpan2: true,
          },
          { name: "rfc", label: "RFC (para factura)" },
          {
            name: "regimenFiscal",
            label: "Régimen fiscal (para factura)",
            type: "select",
            options: regimenesFiscales,
          },
          { name: "notas", label: "Notas", type: "textarea", colSpan2: true },
        ]}
        columnas={[
          { key: "nombre", label: "Nombre" },
          { key: "telefono", label: "Teléfono" },
          { key: "email", label: "Correo" },
          { key: "mascotas", label: "Mascotas", render: (r) => r.mascotas?.length ?? 0 },
        ]}
      />

      <ModalBajaCliente
        cliente={clienteABajar}
        onClose={() => setClienteABajar(null)}
        onBajaExitosa={() => {
          setClienteABajar(null);
          recargarRef.current?.();
        }}
      />
    </>
  );
}
